import { Color } from '@persona-id/design-tokens';

import { traverseWorkflowSteps } from 'dashboard/components/WorkflowEditor/utils';
import { KeyedGraph } from 'dashboard/components/WorkflowEditor/WorkflowCanvas/step-graph';
import {
  WorkflowRunStep,
  WorkflowRunStepLoop,
  WorkflowRunStepStatus,
} from 'dashboard/models/workflow-step';
import {
  outputStep,
  WorkflowStep,
  WorkflowStepConditional,
  WorkflowStepLoop,
  WorkflowStepLoopMapSettings,
  WorkflowStepLoopType,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { ApiModel } from 'lib/interfaces/api';

export type WorkflowRunStepDisplay = {
  status: WorkflowRunStepStatus;
  runStep: WorkflowRunStep | null;
};

type RunStepOrStatusToDisplay = (
  runStepOrStatus: WorkflowRunStep | WorkflowRunStepStatus,
) => WorkflowRunStepDisplay;

const stepDisplayFor = (
  runSteps: WorkflowRunStep[],
  opts: {
    constructStepDisplay: RunStepOrStatusToDisplay;
    wholeGraph: KeyedGraph;
    skippedTokens: string[];
  },
) => {
  return runSteps.reduce<Record<string, WorkflowRunStepDisplay>>((keyed, runStep) => {
    if (opts.skippedTokens.includes(runStep.id)) return keyed;
    return {
      ...keyed,
      [runStep.attributes['config-step-token']]: opts.constructStepDisplay(runStep),
      // When conditional has a route chosen, route name node should be "completed"
      ...('chosen-conditional-index' in runStep.attributes &&
      Number.isInteger(runStep.attributes['chosen-conditional-index']) &&
      opts.wholeGraph[runStep.attributes['config-step-token']]?.step?.type ===
        WorkflowStepType.Conditional
        ? {
            [(
              opts.wholeGraph[runStep.attributes['config-step-token']]
                .step as WorkflowStepConditional
            ).conditionals[runStep.attributes['chosen-conditional-index'] as number].token]:
              opts.constructStepDisplay(WorkflowRunStepStatus.Completed),
          }
        : {}),
      // When loop step is completed, map output should be "completed"
      ...(runStep.type === ApiModel.WorkflowRunStepLoop &&
      (opts.wholeGraph[runStep.attributes['config-step-token']]?.step as WorkflowStepLoop)?.settings
        ?.type === WorkflowStepLoopType.Map &&
      runStep.attributes.status === WorkflowRunStepStatus.Completed
        ? {
            [(
              (opts.wholeGraph[runStep.attributes['config-step-token']].step as WorkflowStepLoop)
                .settings as WorkflowStepLoopMapSettings
            )['output-step-token'] as string]: opts.constructStepDisplay(
              WorkflowRunStepStatus.Completed,
            ),
          }
        : {}),
      // When module step is completed, module output should be "completed"
      ...(runStep.type === ApiModel.WorkflowRunStepModule &&
      runStep.attributes.status === WorkflowRunStepStatus.Completed
        ? {
            [`${runStep.attributes['config-step-token']}-${outputStep.token}`]:
              opts.constructStepDisplay(WorkflowRunStepStatus.Completed),
          }
        : {}),
    };
  }, {});
};

export const buildRenderingRunContext = ({
  enableRunStats,
  keyedGraph,
  lastStepToken,
  runLoopTokensToFocusedCycle,
  runSteps,
  moduleRunSteps,
  inputStepToken,
}: {
  enableRunStats: boolean;
  keyedGraph: KeyedGraph;
  lastStepToken: string;
  runLoopTokensToFocusedCycle: Record<string, number>;
  runSteps: WorkflowRunStep[];
  moduleRunSteps: Record<string, WorkflowRunStep[]>;
  inputStepToken: string;
}): {
  tokenToRunStepDisplay: Record<string, WorkflowRunStepDisplay>;
  tokensForActiveLoopBack: string[];
} => {
  const constructStepDisplay: RunStepOrStatusToDisplay = (runStepOrStatus) => {
    return {
      status:
        typeof runStepOrStatus === 'string' ? runStepOrStatus : runStepOrStatus.attributes.status,
      runStep: enableRunStats && typeof runStepOrStatus !== 'string' ? runStepOrStatus : null,
    };
  };

  const tokensForActiveLoopBack: string[] = [];
  const skippedStepTokensUnderLoop = runSteps
    .filter(
      (runStep): runStep is WorkflowRunStepLoop => runStep.type === ApiModel.WorkflowRunStepLoop,
    )
    .flatMap(({ attributes }) => {
      const currentCycleIndex = runLoopTokensToFocusedCycle[attributes['config-step-token']] ?? 0;
      const { [currentCycleIndex]: current, ...others } = attributes['cycle-to-step-tokens'];

      if (attributes['cycle-to-step-tokens'][currentCycleIndex + 1] != null) {
        tokensForActiveLoopBack.push(attributes['config-step-token']);
      }

      return Object.values(others).flat();
    });

  const allCanceledStepDisplay = Object.keys(keyedGraph).reduce<
    Record<string, WorkflowRunStepDisplay>
  >(
    (accum, token) => ({
      ...accum,
      [token]: constructStepDisplay(WorkflowRunStepStatus.Canceled),
    }),
    {},
  );

  const optsOfStepDisplayFor: Parameters<typeof stepDisplayFor>[1] = {
    wholeGraph: keyedGraph,
    skippedTokens: skippedStepTokensUnderLoop,
    constructStepDisplay,
  };
  const stepDisplayForMainRun = stepDisplayFor(runSteps, optsOfStepDisplayFor);
  const stepDisplayForModuleRuns = Object.entries(moduleRunSteps).reduce(
    (accum, [moduleConfigStepToken, runStepsPerModule]) => {
      const prefixed = runStepsPerModule.map((m) => ({
        ...m,
        attributes: {
          ...m.attributes,
          'config-step-token': `${moduleConfigStepToken}-${m.attributes['config-step-token']}`,
        },
      }));

      return {
        ...accum,
        ...stepDisplayFor(prefixed, optsOfStepDisplayFor),
      };
    },
    {},
  );

  return {
    tokenToRunStepDisplay: {
      ...allCanceledStepDisplay,
      ...stepDisplayForMainRun,
      ...stepDisplayForModuleRuns,
      [inputStepToken]: constructStepDisplay(WorkflowRunStepStatus.Completed),
      [outputStep.token]: constructStepDisplay(
        stepDisplayForMainRun[lastStepToken]?.status === WorkflowRunStepStatus.Completed
          ? WorkflowRunStepStatus.Completed
          : WorkflowRunStepStatus.Canceled,
      ),
    },
    tokensForActiveLoopBack,
  };
};

const stepAccentColorFor = ({
  deactivated,
  errored,
  selected,
  status,
  surrounded,
}: {
  deactivated: boolean;
  errored: boolean;
  selected: boolean;
  status: WorkflowRunStepStatus | null;
  surrounded: boolean;
}): Color => {
  const active = selected || surrounded;
  if (status == null) {
    if (deactivated) {
      return active ? Color.BorderIconPrimary : Color.BorderIconSecondary;
    }
    return errored
      ? active
        ? Color.CoreRed900
        : Color.CoreRed800
      : active
        ? Color.CorePurple800
        : Color.CorePurple200;
  } else {
    switch (status) {
      case WorkflowRunStepStatus.Completed:
        return active ? Color.CorePurple900 : Color.CorePurple600;
      case WorkflowRunStepStatus.Errored:
      case WorkflowRunStepStatus.Failed:
        return active ? Color.CoreRed900 : Color.CoreRed600;
      case WorkflowRunStepStatus.InProgress:
        return active ? Color.CoreYellow600 : Color.CoreYellow400;
      default:
        return active ? Color.CorePurple800 : Color.CorePurple200;
    }
  }
};

const effectFor = ({
  dragged,
  status,
}: {
  dragged: boolean;
  status: WorkflowRunStepStatus | null;
}) => {
  if (
    [WorkflowRunStepStatus.Initialized, WorkflowRunStepStatus.Canceled].some(
      (expectedStatus) => expectedStatus === status,
    ) ||
    dragged
  ) {
    return 'opacity(0.4)';
  } else {
    return '';
  }
};

export const stepDecorationsFor = ({
  deactivated,
  dragged,
  errored,
  selected,
  status,
  surrounded,
}: {
  deactivated: boolean;
  dragged: boolean;
  errored: boolean;
  selected: boolean;
  status: WorkflowRunStepStatus | null;
  surrounded: boolean;
}): { accentColor: Color; filterEffect: string } => ({
  accentColor: stepAccentColorFor({ deactivated, errored, selected, status, surrounded }),
  filterEffect: effectFor({ dragged, status }),
});

// all nested steps and conditionals get its parent module's token as a prefix
export const distinctNestedNodeTokens = (moduleToBlockSteps: Record<string, WorkflowStep[]>) =>
  Object.entries(moduleToBlockSteps).reduce<typeof moduleToBlockSteps>(
    (acc, [moduleToken, nestedSteps]) => ({
      ...acc,
      [moduleToken]: traverseWorkflowSteps(nestedSteps, (nestedStep) => ({
        step: {
          ...nestedStep,
          token: [moduleToken, nestedStep.token].join('-'),
          ...(nestedStep.type === WorkflowStepType.Conditional
            ? {
                conditionals: nestedStep.conditionals.map((c) => ({
                  ...c,
                  token: [moduleToken, c.token].join('-'),
                })),
              }
            : {}),
        },
      })),
    }),
    {},
  );
